import { Button, Input } from "@garther/ui/components";
import { useRouter } from "@tanstack/react-router";
import { Html5Qrcode } from "html5-qrcode";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface ScannerPageProps {
  onScanSuccess: (decodedText: string) => void;
  onScanError?: (error: string) => void;
}

export function ScannerPage({ onScanSuccess, onScanError }: ScannerPageProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualInput, setManualInput] = useState<string>("");
  const router = useRouter();

  const handleManualInputSubmit = () => {
    if (manualInput.trim()) {
      onScanSuccess(manualInput.trim());
    }
    setManualInput("");
  };

  const handleClose = () => {
    router.history.back();
  };

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    // Initialize scanner when modal opens
    const scannerId = containerRef.current.id;
    const html5QrCode = new Html5Qrcode(scannerId);

    const startScanning = async () => {
      try {
        setError(null);
        setIsScanning(false);
        await html5QrCode.start(
          { facingMode: "environment" }, // Use back camera
          {
            fps: 5,
            qrbox: {
              width: 300,
              height: 300
            },
            aspectRatio: 1.0,
            disableFlip: true
          },
          (decodedText) => {
            // Success callback
            onScanSuccess(decodedText);
            // // Stop scanning after successful scan
            // html5QrCode.stop().then(() => {
            //   html5QrCode.clear();
            //   scannerRef.current = null;
            //   setIsScanning(false);
            // });
          },
          (errorMessage) => {
            // Error callback - ignore most errors as they're just "no QR code found" messages
            if (errorMessage && !errorMessage.includes("NotFoundException")) {
              if (onScanError) {
                onScanError(errorMessage);
              }
            }
          }
        );
        scannerRef.current = html5QrCode;
        setIsScanning(true);
      } catch (err: any) {
        console.error("Error starting scanner:", err);
        const errorMsg = err.message || "Failed to start camera";
        setError(errorMsg);
        setIsScanning(false);
        if (onScanError) {
          onScanError(errorMsg);
        }
      }
    };

    startScanning();

    return () => {
      // Cleanup on unmount or when dependencies change
      if (scannerRef.current) {
        scannerRef.current
          .stop()
          .then(() => {
            scannerRef.current?.clear();
            scannerRef.current = null;
            setIsScanning(false);
          })
          .catch((err) => {
            console.error("Error stopping scanner:", err);
            scannerRef.current = null;
            setIsScanning(false);
          });
      }
    };
  }, [onScanSuccess, onScanError]);

  return (
    <div className="w-screen h-screen flex flex-col items-center relative bg-background-secondary">
      <div className="text-center max-w-md w-full mt-6 gap-4 flex flex-col">
        <div>
          <h2 className="text-responsive-lg font-medium text-text-primary">Check in with QR code</h2>
          <p className="text-responsive-sm text-text-secondary">Scan the QR code to check in</p>
        </div>

        <div className="px-4 w-full flex items-stretch gap-2">
          <Input
            containerClassName="flex-1"
            placeholder="Or enter ticket ID manually"
            value={manualInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualInput(e.target.value)}
          />
          <Button onClick={handleManualInputSubmit}>
            <ArrowRight size={18} />
          </Button>
        </div>
        <div
          ref={containerRef}
          id="qr-reader"
          className="overflow-hidden w-screen max-w-[450px] aspect-square"
        />
        {!isScanning && !error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-text-secondary">Initializing camera...</p>
          </div>
        )}
        <Button
          className="w-full bg-red-500 text-responsive-md font-semibold"
          padding="py-4"
          onClick={handleClose}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
